import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createCategoryPrice,
  deleteCategoryPrices,
  endCategoryPrices,
  getBasePriceTimeline,
  getBizestCategories,
  getPriceCodes,
  searchBasePrices,
  searchCategoryPrices,
  updateCategoryPrice,
} from '@/services/settlement/settlementPricing/api'
import {
  BasePriceListReq,
  CategoryPriceCreateReq,
  CategoryPriceListReq,
  CategoryPriceUpdateReq,
} from '@/services/settlement/settlementPricing/types'

export const settlementPricingQueryKeys = {
  all: ['SETTLEMENT_PRICING'] as const,
  codes: () => [...settlementPricingQueryKeys.all, 'codes'] as const,
  baseList: (params: ApiPaginationReq<BasePriceListReq>) => [...settlementPricingQueryKeys.all, 'base', 'list', params] as const,
  categoryList: (params: ApiPaginationReq<CategoryPriceListReq>) => [...settlementPricingQueryKeys.all, 'category', 'list', params] as const,
}

const emptyPage = { page: 0, size: 0, totalCount: 0, totalPages: 0 }

// FE 전용 노출 제외 서비스 이용항목 — BE 응답엔 존재하나 단가관리 화면에서만 숨긴다 (기타조정비/반출비)
const FE_HIDDEN_SERVICE_TYPE_CODES = ['ETC_ADJUSTMENT', 'RELEASE']

// ── 공통 코드 ──────────────────────────────────
export const useGetPriceCodes = () =>
  useQuery({
    queryKey: settlementPricingQueryKeys.codes(),
    queryFn: getPriceCodes,
    select: ({ data }) => ({
      ...data,
      serviceTypes: (data?.serviceTypes ?? []).filter((s) => !FE_HIDDEN_SERVICE_TYPE_CODES.includes(s.code)),
    }),
    staleTime: 1000 * 60 * 10,
  })

// ── 탭① 기본단가 ──────────────────────────────
export const useSearchBasePrices = (params: ApiPaginationReq<BasePriceListReq>) =>
  useQuery({
    queryKey: settlementPricingQueryKeys.baseList(params),
    queryFn: () => searchBasePrices(params),
    select: ({ data }) => ({ items: data?.items ?? [], pageInfo: data?.pageInfo ?? emptyPage }),
    enabled: false,
  })


// 기본단가 변경 이력(조회전용) — 행 클릭 모달. masterId 있을 때만 fetch.
export const useGetBasePriceTimeline = (masterId: number | null) =>
  useQuery({
    queryKey: [...settlementPricingQueryKeys.all, 'base', 'timeline', masterId] as const,
    queryFn: () => getBasePriceTimeline(masterId as number),
    select: ({ data }) => data,
    enabled: masterId !== null,
  })

// bizest 대분류 필터 옵션 (category_depth_1 고유 목록)
export const useGetBizestCategories = () =>
  useQuery({
    queryKey: [...settlementPricingQueryKeys.all, 'bizest-categories'] as const,
    queryFn: getBizestCategories,
    select: ({ data }) => data?.items ?? [],
    staleTime: 1000 * 60 * 10,
  })

// ── 탭② 카테고리 단가 ─────────────────────────
export const useSearchCategoryPrices = (params: ApiPaginationReq<CategoryPriceListReq>) =>
  useQuery({
    queryKey: settlementPricingQueryKeys.categoryList(params),
    queryFn: () => searchCategoryPrices(params),
    select: ({ data }) => ({ items: data?.items ?? [], pageInfo: data?.pageInfo ?? emptyPage }),
    enabled: false,
  })

export const useCreateCategoryPrice = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: CategoryPriceCreateReq) => createCategoryPrice(params),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settlementPricingQueryKeys.all }),
  })
}

export const useUpdateCategoryPrice = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: CategoryPriceUpdateReq) => updateCategoryPrice(params),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settlementPricingQueryKeys.all }),
  })
}

export const useEndCategoryPrices = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: { ids: number[] }) => endCategoryPrices(params),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settlementPricingQueryKeys.all }),
  })
}

export const useDeleteCategoryPrices = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: { ids: number[] }) => deleteCategoryPrices(params),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settlementPricingQueryKeys.all }),
  })
}
